import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Cake, MagnifyingGlass, Confetti, Calendar } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const monthLabel = (m) => MONTHS_PT[m - 1] || `Mes ${m}`;

const fmtDM = (d, m) => `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;

export default function Birthdays() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/birthdays`, { withCredentials: true });
      setData(data);
    } catch (e) {
      console.error('Birthdays load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = search.toLowerCase();
    if (!term) return data.all_birthdays;
    return data.all_birthdays.filter((b) =>
      (b.name || '').toLowerCase().includes(term) ||
      (b.email || '').toLowerCase().includes(term)
    );
  }, [data, search]);

  // Group filtered by month
  const grouped = useMemo(() => {
    const map = {};
    for (const b of filtered) {
      if (!map[b.month]) map[b.month] = [];
      map[b.month].push(b);
    }
    return map;
  }, [filtered]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      </Layout>
    );
  }

  const currentMonth = data.current_month;
  const monthsInOrder = Array.from({ length: 12 }, (_, i) => ((currentMonth - 1 + i) % 12) + 1);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="birthdays-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Cake size={28} className="text-[#E85D04]" weight="fill" /> Aniversariantes
            </h1>
            <p className="text-[#73706A] mt-1">{data.total_with_birth_date} alunos com data cadastrada de {data.total_students}</p>
          </div>
          <div className="relative max-w-md flex-1 sm:max-w-xs">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input placeholder="Buscar por nome ou email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="birthdays-search" />
          </div>
        </div>

        {/* Current month highlight */}
        <div className="bg-gradient-to-br from-[#9B2226] to-[#E85D04] rounded-xl p-5 sm:p-6 text-white shadow-lg" data-testid="current-month-card">
          <div className="flex items-center gap-3 mb-3">
            <Confetti size={28} weight="fill" />
            <div>
              <h2 className="font-['Outfit'] text-xl sm:text-2xl font-bold">{monthLabel(currentMonth)}</h2>
              <p className="text-xs opacity-90">{data.current_month_count} aniversariante{data.current_month_count !== 1 ? 's' : ''} este mes</p>
            </div>
          </div>
          {data.current_month_birthdays.length === 0 ? (
            <p className="text-sm opacity-90">Nenhum aniversariante neste mes.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="current-month-list">
              {data.current_month_birthdays.map((b) => (
                <Link
                  key={b.id}
                  to={`/students/${b.id}`}
                  className={`bg-white/95 hover:bg-white text-[#1A1A1A] rounded-lg p-3 transition-transform hover:scale-[1.02] ${b.is_today ? 'ring-4 ring-[#FFD60A] shadow-lg' : ''}`}
                  data-testid={`current-month-row-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-['Outfit'] font-bold text-base truncate">{b.name}</span>
                        {b.is_today && (
                          <span className="bg-[#FFD60A] text-[#1A1A1A] text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid="today-badge">HOJE!</span>
                        )}
                      </div>
                      <p className="text-xs text-[#73706A] truncate">{b.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-['Outfit'] font-bold text-lg text-[#E85D04]">{fmtDM(b.day, b.month)}</p>
                      {b.turning_age != null && b.turning_age > 0 && <p className="text-[10px] text-[#73706A]">{b.turning_age} anos</p>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* All months */}
        <div className="space-y-4">
          {monthsInOrder.map((m) => {
            const list = grouped[m] || [];
            if (list.length === 0) return null;
            const isCurrent = m === currentMonth;
            return (
              <div key={m} className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden" data-testid={`month-section-${m}`}>
                <div className={`px-4 py-3 flex items-center justify-between ${isCurrent ? 'bg-[#FFF3E6]' : 'bg-[#F5F2EB]'}`}>
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className={isCurrent ? 'text-[#E85D04]' : 'text-[#73706A]'} weight={isCurrent ? 'fill' : 'regular'} />
                    <h3 className={`font-['Outfit'] font-bold text-base ${isCurrent ? 'text-[#E85D04]' : 'text-[#1A1A1A]'}`}>{monthLabel(m)}</h3>
                    {isCurrent && <span className="bg-[#E85D04] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">Mes atual</span>}
                  </div>
                  <span className="text-xs text-[#73706A] font-semibold">{list.length} aniversariante{list.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="divide-y divide-[#E6E2D8]">
                  {list.map((b) => (
                    <Link
                      key={b.id}
                      to={`/students/${b.id}`}
                      className="flex items-center justify-between p-3 hover:bg-[#FDFBF7] transition-colors"
                      data-testid={`birthday-row-${b.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrent ? 'bg-[#E85D04]' : 'bg-[#0077B6]'}`}>
                          <Cake size={16} className="text-white" weight="fill" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[#1A1A1A] truncate">{b.name}</p>
                          <p className="text-xs text-[#73706A] truncate">{b.email} {b.phone ? `• ${b.phone}` : ''}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-['Outfit'] font-semibold text-[#1A1A1A]">{fmtDM(b.day, b.month)}</p>
                        {b.turning_age != null && b.turning_age > 0 && <p className="text-[10px] text-[#73706A]">{b.turning_age} anos</p>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center text-[#73706A]" data-testid="birthdays-empty">
              {search ? 'Nenhum aniversariante encontrado para essa busca' : 'Nenhum aluno com data de nascimento cadastrada'}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
